import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useDataProtectionIncreaseDays } from "@/queries/use-data-protection.query.ts";
import {
  ApiDataProtectionIncreaseDaysPayload,
  DataProtectionTypes,
} from "@/types/data-protection.types.ts";
import { Button, Input, useModals, Modal, ModalProps, Stack } from "@skailer-com/uikit";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface DataProtectionIncreasePeriod extends ModalProps {
  objectId: number;
  type: DataProtectionTypes;
}

export function DataProtectionIncreasePeriod({
  objectId,
  type,
  ...props
}: DataProtectionIncreasePeriod) {
  const { t } = useTranslation();
  const dataProtectionIncreaseDaysSchema = z.object({
    data_protection_type: z.enum(DataProtectionTypes),
    additional_days: z
      .number({ error: t("api_errors:must_be_number") })
      .int(t("api_errors:must_integer"))
      .min(1, t("api_errors:minimum_value_must", { minimum: 1 }))
      .max(3650, t("api_errors:maximum_value_must", { maximum: 3650 })),
  });
  const { close } = useModals();
  const { id } = props;

  type DataProtectionIncreaseDaysSchema = z.infer<typeof dataProtectionIncreaseDaysSchema>;

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DataProtectionIncreaseDaysSchema>({
    mode: "onSubmit",
    resolver: zodResolver(dataProtectionIncreaseDaysSchema),
    defaultValues: {
      data_protection_type: type,
    },
  });

  const request = useDataProtectionIncreaseDays(control);

  const onSubmit = handleSubmit(async (schema) => {
    const payload: ApiDataProtectionIncreaseDaysPayload = {
      additional_days: schema.additional_days,
      data_protection_type: schema.data_protection_type,
    };

    await request.mutateAsync({ objectId, payload });

    close(id);
  });

  return (
    <Modal
      {...props}
      title={t("data_protection:increase_manually_description")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} isLoading={request.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <Input
        label={t("data_protection:increase_manually_enter_days")}
        type={"number"}
        {...register("additional_days", {
          setValueAs: (value) => (value === "" ? undefined : Number(value)),
        })}
        error={errors.additional_days?.message}
      />
    </Modal>
  );
}
